import React, { useState, useEffect, useCallback } from 'react';

interface Company {
  id: string;
  name: string;
  slug: string;
  description?: string;
  website?: string;
  isActive: boolean;
  userCount?: number;
  createdAt: string;
  updatedAt: string;
}

interface CompanyUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

interface AddUserForm {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

const EMPTY_ADD_USER: AddUserForm = { email: '', firstName: '', lastName: '', role: 'COMPANY_USER' };

interface FormData {
  name: string;
  slug: string;
  description: string;
  website: string;
}

const EMPTY_FORM: FormData = { name: '', slug: '', description: '', website: '' };

function authHeaders() {
  const token = localStorage.getItem('admin_access_token');
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiFetch(url: string, options: RequestInit = {}) {
  const res = await fetch(url, { ...options, headers: { ...authHeaders(), ...(options.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error?.message || `Request failed (${res.status})`);
  return body;
}

const formatDate = (s: string) =>
  new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

const roleLabel = (role: string) =>
  role === 'COMPANY_ADMIN' ? 'Admin' : role === 'COMPANY_USER' ? 'User' : role;

export default function CompanyManagementPage() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal: 'add' | 'edit' | 'users' | null
  const [modal, setModal] = useState<'add' | 'edit' | 'users' | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [viewingCompany, setViewingCompany] = useState<Company | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  // Form state — four separate concerns
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<FormData>>({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [slugUnlocked, setSlugUnlocked] = useState(false);

  // Add-user sub-form (inside users modal)
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserForm, setAddUserForm] = useState<AddUserForm>(EMPTY_ADD_USER);
  const [addUserFieldErrors, setAddUserFieldErrors] = useState<Partial<AddUserForm>>({});
  const [addUserApiError, setAddUserApiError] = useState('');
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  const loadCompanies = useCallback(async () => {
    setIsLoading(true);
    setPageError('');
    try {
      const result = await apiFetch('/api/admin/companies');
      const list: Company[] = result.data || [];
      const withCounts = await Promise.all(
        list.map(async (c) => {
          try {
            const r = await apiFetch(`/api/admin/companies/${c.id}/users`);
            return { ...c, userCount: (r.data || []).length };
          } catch {
            return { ...c, userCount: 0 };
          }
        })
      );
      setCompanies(withCounts);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = modal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modal]);

  // Single generic change handler — reads name from the element
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name as keyof FormData]) setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    if (apiError) setApiError('');
  };

  const validate = (): boolean => {
    const errors: Partial<FormData> = {};
    if (!formData.name.trim()) errors.name = 'Company name is required';
    if (modal === 'edit' && slugUnlocked) {
      if (!formData.slug.trim()) {
        errors.slug = 'Slug is required';
      } else if (!/^[a-z0-9-]+$/.test(formData.slug.trim())) {
        errors.slug = 'Only lowercase letters, numbers, and hyphens';
      } else if (formData.slug.trim().length < 3) {
        errors.slug = 'Must be at least 3 characters';
      }
    }
    if (formData.website.trim() && !/^https?:\/\/.+/.test(formData.website.trim())) {
      errors.website = 'Must start with http:// or https://';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError('');
    try {
      const payload: Record<string, string> = { name: formData.name.trim() };
      if (modal === 'edit' && slugUnlocked) payload.slug = formData.slug.trim();
      if (formData.description.trim()) payload.description = formData.description.trim();
      if (formData.website.trim()) payload.website = formData.website.trim();

      if (modal === 'edit' && editingCompany) {
        await apiFetch(`/api/admin/companies/${editingCompany.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/admin/companies', { method: 'POST', body: JSON.stringify(payload) });
      }
      await loadCompanies();
      closeModal();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to save company');
    } finally {
      setSubmitting(false);
    }
  };

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setFieldErrors({});
    setApiError('');
    setEditingCompany(null);
    setModal('add');
  };

  const openEdit = (company: Company) => {
    setFormData({ name: company.name, slug: company.slug, description: company.description || '', website: company.website || '' });
    setFieldErrors({});
    setApiError('');
    setSlugUnlocked(false);
    setEditingCompany(company);
    setModal('edit');
  };

  const openUsers = async (company: Company) => {
    setViewingCompany(company);
    setCompanyUsers([]);
    setIsLoadingUsers(true);
    setShowAddUser(false);
    setAddUserForm(EMPTY_ADD_USER);
    setAddUserFieldErrors({});
    setAddUserApiError('');
    setTempPassword('');
    setModal('users');
    try {
      const result = await apiFetch(`/api/admin/companies/${company.id}/users`);
      setCompanyUsers(result.data || []);
    } catch {
      setCompanyUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAddUserChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAddUserForm(prev => ({ ...prev, [name]: value }));
    if (addUserFieldErrors[name as keyof AddUserForm]) setAddUserFieldErrors(prev => ({ ...prev, [name]: undefined }));
    if (addUserApiError) setAddUserApiError('');
  };

  const handleAddUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Partial<AddUserForm> = {};
    if (!addUserForm.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addUserForm.email.trim())) errors.email = 'Enter a valid email';
    setAddUserFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setAddUserSubmitting(true);
    setAddUserApiError('');
    try {
      const result = await apiFetch(`/api/admin/companies/${viewingCompany!.id}/users`, {
        method: 'POST',
        body: JSON.stringify({
          email: addUserForm.email.trim(),
          firstName: addUserForm.firstName.trim() || undefined,
          lastName: addUserForm.lastName.trim() || undefined,
          role: addUserForm.role,
        }),
      });
      setTempPassword(result.data.temporaryPassword);
      setCompanyUsers(prev => [...prev, result.data.user]);
      setAddUserForm(EMPTY_ADD_USER);
      setShowAddUser(false);
      // Refresh company user count
      loadCompanies();
    } catch (err) {
      setAddUserApiError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const closeModal = () => {
    setModal(null);
    setEditingCompany(null);
    setViewingCompany(null);
    setCompanyUsers([]);
    setFieldErrors({});
    setApiError('');
    setSlugUnlocked(false);
    setShowAddUser(false);
    setAddUserForm(EMPTY_ADD_USER);
    setAddUserFieldErrors({});
    setAddUserApiError('');
    setTempPassword('');
  };

  const handleStatusChange = async (companyId: string, activate: boolean) => {
    try {
      await apiFetch(`/api/admin/companies/${companyId}/${activate ? 'reactivate' : 'deactivate'}`, { method: 'POST' });
      await loadCompanies();
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const filtered = companies.filter(c => {
    const matchSearch =
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && c.isActive) ||
      (statusFilter === 'inactive' && !c.isActive);
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h3 fw-bold text-dark mb-1">Company Management</h1>
          <p className="text-muted mb-0">Manage all companies on the platform</p>
        </div>
        <button className="btn btn-admin" onClick={openAdd}>
          <i className="bi bi-plus-lg me-1"></i>Add Company
        </button>
      </div>

      {pageError && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          <i className="bi bi-exclamation-circle me-2"></i>{pageError}
          <button type="button" className="btn-close" onClick={() => setPageError('')}></button>
        </div>
      )}

      <div className="admin-search-bar">
        <div className="row g-3 align-items-end">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text"><i className="bi bi-search"></i></span>
              <input
                type="text"
                className="form-control"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="col-md-3">
            <div className="d-flex gap-2">
              <span className="badge bg-success">{companies.filter(c => c.isActive).length} Active</span>
              <span className="badge bg-danger">{companies.filter(c => !c.isActive).length} Inactive</span>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-table">
        {isLoading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="text-center">
              <div className="spinner-border admin-spinner mb-3" role="status"></div>
              <p className="text-muted">Loading companies...</p>
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-building text-muted" style={{ fontSize: '3rem' }}></i>
            <h6 className="mt-3 text-muted">No companies found</h6>
            <p className="text-muted">
              {searchTerm || statusFilter !== 'all' ? 'No companies match your search' : 'No companies added yet'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button className="btn btn-admin mt-2" onClick={openAdd}>
                <i className="bi bi-plus-lg me-1"></i>Add Your First Company
              </button>
            )}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Slug</th>
                  <th>Website</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((company) => (
                  <tr key={company.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                          style={{ width: 40, height: 40, fontSize: '0.875rem' }}
                        >
                          {company.name.charAt(0)}
                        </div>
                        <div>
                          <div className="fw-semibold">{company.name}</div>
                          {company.description && <small className="text-muted">{company.description}</small>}
                        </div>
                      </div>
                    </td>
                    <td><code className="small">{company.slug}</code></td>
                    <td>
                      {company.website
                        ? <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none small"><i className="bi bi-link-45deg"></i> Visit</a>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <button className="btn btn-link p-0 text-decoration-none" onClick={() => openUsers(company)}>
                        <span className="fw-semibold">{company.userCount ?? 0}</span>
                        <small className="text-muted ms-1">users</small>
                      </button>
                    </td>
                    <td>
                      <div className="dropdown">
                        <button
                          className={`badge border-0 dropdown-toggle ${company.isActive ? 'bg-success' : 'bg-danger'}`}
                          data-bs-toggle="dropdown"
                        >
                          {company.isActive ? 'Active' : 'Inactive'}
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button className="dropdown-item" disabled={company.isActive} onClick={() => handleStatusChange(company.id, true)}>
                              <i className="bi bi-check-circle text-success me-2"></i>Activate
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item" disabled={!company.isActive} onClick={() => handleStatusChange(company.id, false)}>
                              <i className="bi bi-pause-circle text-danger me-2"></i>Deactivate
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                    <td><small className="text-muted">{formatDate(company.createdAt)}</small></td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="admin-action-btn btn-view" title="View Users" onClick={() => openUsers(company)}>
                          <i className="bi bi-people"></i>
                        </button>
                        <button className="admin-action-btn btn-edit" title="Edit" onClick={() => openEdit(company)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content admin-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className={`bi ${modal === 'add' ? 'bi-building' : 'bi-pencil'} me-2`}></i>
                  {modal === 'add' ? 'Add New Company' : `Edit: ${editingCompany?.name}`}
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="modal-body">
                  {apiError && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-circle me-2"></i>{apiError}
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="f-name" className="form-label fw-semibold">
                      Company Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="f-name"
                      name="name"
                      type="text"
                      className={`form-control ${fieldErrors.name ? 'is-invalid' : ''}`}
                      value={formData.name}
                      onChange={handleChange}
                      autoFocus
                    />
                    {fieldErrors.name && <div className="invalid-feedback">{fieldErrors.name}</div>}
                  </div>

                  {modal === 'edit' && (
                    <div className="mb-3">
                      <label htmlFor="f-slug" className="form-label fw-semibold">Slug</label>
                      <input
                        id="f-slug"
                        name="slug"
                        type="text"
                        className={`form-control font-monospace ${fieldErrors.slug ? 'is-invalid' : ''}`}
                        value={formData.slug}
                        onChange={handleChange}
                        disabled={!slugUnlocked}
                      />
                      {fieldErrors.slug && <div className="invalid-feedback">{fieldErrors.slug}</div>}
                      <div className="mt-2">
                        <div className={`p-2 rounded border ${slugUnlocked ? 'border-warning bg-warning bg-opacity-10' : 'border-0 bg-light'}`}>
                          <div className="form-check mb-0">
                            <input
                              id="f-slug-unlock"
                              type="checkbox"
                              className="form-check-input"
                              checked={slugUnlocked}
                              onChange={(e) => {
                                setSlugUnlocked(e.target.checked);
                                if (!e.target.checked) {
                                  setFormData(prev => ({ ...prev, slug: editingCompany?.slug ?? '' }));
                                  setFieldErrors(prev => ({ ...prev, slug: undefined }));
                                }
                              }}
                            />
                            <label className="form-check-label small" htmlFor="f-slug-unlock">
                              {slugUnlocked
                                ? <><i className="bi bi-exclamation-triangle-fill text-warning me-1"></i><strong>Slug editing is enabled.</strong> Changing the slug will break existing QR codes, sign links, and any integrations using the current slug.</>
                                : <span className="text-muted">I want to change the slug — I understand this will break existing QR codes and integrations</span>
                              }
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="f-description" className="form-label fw-semibold">Description</label>
                    <textarea
                      id="f-description"
                      name="description"
                      className="form-control"
                      rows={3}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Brief description of the company"
                    />
                  </div>

                  <div className="mb-1">
                    <label htmlFor="f-website" className="form-label fw-semibold">Website</label>
                    <input
                      id="f-website"
                      name="website"
                      type="url"
                      className={`form-control ${fieldErrors.website ? 'is-invalid' : ''}`}
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://example.com"
                    />
                    {fieldErrors.website && <div className="invalid-feedback">{fieldErrors.website}</div>}
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-admin" disabled={submitting}>
                    {submitting
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>{modal === 'add' ? 'Creating...' : 'Saving...'}</>
                      : <><i className={`bi ${modal === 'add' ? 'bi-plus-lg' : 'bi-check-lg'} me-1`}></i>{modal === 'add' ? 'Create Company' : 'Save Changes'}</>
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Users Modal */}
      {modal === 'users' && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content admin-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-people me-2"></i>{viewingCompany?.name} — Users
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                {/* Temp password banner — shown after successful user creation */}
                {tempPassword && (
                  <div className="alert alert-success d-flex align-items-start gap-2 mb-3">
                    <i className="bi bi-key-fill flex-shrink-0 mt-1"></i>
                    <div>
                      <strong>User created.</strong> Share this temporary password with them — it won't be shown again:
                      <div className="mt-1">
                        <code className="fs-6 user-select-all">{tempPassword}</code>
                      </div>
                    </div>
                    <button type="button" className="btn-close ms-auto" onClick={() => setTempPassword('')}></button>
                  </div>
                )}

                {/* Add User inline form */}
                {showAddUser && (
                  <div className="card border mb-3">
                    <div className="card-header d-flex align-items-center justify-content-between py-2">
                      <span className="fw-semibold small">New User</span>
                      <button type="button" className="btn-close btn-sm" onClick={() => { setShowAddUser(false); setAddUserForm(EMPTY_ADD_USER); setAddUserFieldErrors({}); setAddUserApiError(''); }}></button>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleAddUserSubmit} noValidate>
                        {addUserApiError && (
                          <div className="alert alert-danger py-2 small" role="alert">
                            <i className="bi bi-exclamation-circle me-1"></i>{addUserApiError}
                          </div>
                        )}
                        <div className="row g-2">
                          <div className="col-md-5">
                            <label htmlFor="au-email" className="form-label form-label-sm fw-semibold">
                              Email <span className="text-danger">*</span>
                            </label>
                            <input
                              id="au-email"
                              name="email"
                              type="email"
                              className={`form-control form-control-sm ${addUserFieldErrors.email ? 'is-invalid' : ''}`}
                              value={addUserForm.email}
                              onChange={handleAddUserChange}
                              autoFocus
                            />
                            {addUserFieldErrors.email && <div className="invalid-feedback">{addUserFieldErrors.email}</div>}
                          </div>
                          <div className="col-md-2">
                            <label htmlFor="au-firstName" className="form-label form-label-sm fw-semibold">First Name</label>
                            <input
                              id="au-firstName"
                              name="firstName"
                              type="text"
                              className="form-control form-control-sm"
                              value={addUserForm.firstName}
                              onChange={handleAddUserChange}
                            />
                          </div>
                          <div className="col-md-2">
                            <label htmlFor="au-lastName" className="form-label form-label-sm fw-semibold">Last Name</label>
                            <input
                              id="au-lastName"
                              name="lastName"
                              type="text"
                              className="form-control form-control-sm"
                              value={addUserForm.lastName}
                              onChange={handleAddUserChange}
                            />
                          </div>
                          <div className="col-md-2">
                            <label htmlFor="au-role" className="form-label form-label-sm fw-semibold">Role</label>
                            <select
                              id="au-role"
                              name="role"
                              className="form-select form-select-sm"
                              value={addUserForm.role}
                              onChange={handleAddUserChange}
                            >
                              <option value="COMPANY_USER">User</option>
                              <option value="COMPANY_ADMIN">Admin</option>
                            </select>
                          </div>
                          <div className="col-md-1 d-flex align-items-end">
                            <button type="submit" className="btn btn-admin btn-sm w-100" disabled={addUserSubmitting}>
                              {addUserSubmitting
                                ? <span className="spinner-border spinner-border-sm"></span>
                                : <i className="bi bi-check-lg"></i>
                              }
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Actions row */}
                {!showAddUser && (
                  <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-admin btn-sm" onClick={() => { setShowAddUser(true); setTempPassword(''); }}>
                      <i className="bi bi-person-plus me-1"></i>Add User
                    </button>
                  </div>
                )}

                {isLoadingUsers ? (
                  <div className="d-flex justify-content-center py-5">
                    <div className="spinner-border admin-spinner" role="status"></div>
                  </div>
                ) : companyUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bi bi-people text-muted" style={{ fontSize: '3rem' }}></i>
                    <p className="mt-3 text-muted">No users in this company yet</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUsers.map((user) => (
                          <tr key={user.id}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div
                                  className="bg-secondary rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                  style={{ width: 32, height: 32, fontSize: '0.75rem' }}
                                >
                                  {(user.firstName?.[0] || user.email[0]).toUpperCase()}
                                </div>
                                <span className="fw-semibold">
                                  {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'No name'}
                                </span>
                              </div>
                            </td>
                            <td>{user.email}</td>
                            <td><span className="badge bg-info">{roleLabel(user.role)}</span></td>
                            <td>
                              <span className={`badge ${user.isActive ? 'bg-success' : 'bg-danger'}`}>
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td><small className="text-muted">{formatDate(user.createdAt)}</small></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
